import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import {
  CREATE_COMPANY_COMMENT_MUTATION,
  UPDATE_COMPANY_COMMENT_MUTATION,
  PUBLIC_CLIENT_LIST_QUERY,
  LIST_CONTACT_SUBJECTS_QUERY
} from '../../pages/clients/gql';
import { dispatchMessage, dispatchException } from 'helper/snackbar';
import { userSelector } from 'helper/security';
import { Comment } from './types';
import { CONTACT_SUBJECTS } from './subjects';

interface CommentDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  companyId: number;
  clientId?: number;
  editingComment?: Comment | null;
}

/**
 * Dialog for creating/editing company/client comments.
 * Supports booking relevance, contact relevance with subject selection, and contact type.
 * Validates description (min 10 chars) and contact subject when contact-relevant.
 */
const CommentDialog: React.FC<CommentDialogProps> = ({
  open,
  onClose,
  onSaved,
  companyId,
  clientId,
  editingComment,
}) => {
  const dispatch = useDispatch();
  const currentUser = userSelector();
  const [description, setDescription] = useState('');
  const [isBookingRelevant, setIsBookingRelevant] = useState(false);
  const [isContactRelevant, setIsContactRelevant] = useState(false);
  const [contactSubjectId, setContactSubjectId] = useState<number | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ description?: string; contactSubject?: string }>({});
  const [contactType, setContactType] = useState<'INFO' | 'PERS' | 'EMAIL' | 'TEL'>('INFO');

  const contactTypeOptions = [
    { value: 'INFO', labelKey: 'comment-contact-type-info' },
    { value: 'PERS', labelKey: 'comment-contact-type-personal' },
    { value: 'EMAIL', labelKey: 'comment-contact-type-email' },
    { value: 'TEL', labelKey: 'comment-contact-type-phone' },
  ] as const;

  // Query for contact subjects
  const { data: contactSubjectsData, refetch: refetchContactSubjects } = useQuery(LIST_CONTACT_SUBJECTS_QUERY, {
    variables: {
      where: {
        isActive: true
      }
    },
  });

  const contactSubjects = contactSubjectsData?.listContactSubjects || [];

  const [createCommentMutation] = useMutation(CREATE_COMPANY_COMMENT_MUTATION, {
    refetchQueries: ['ListCompanyComments'],
  });
  const [updateCommentMutation] = useMutation(UPDATE_COMPANY_COMMENT_MUTATION, {
    refetchQueries: ['ListCompanyComments'],
  });

  // Query for active contacts of the company
  const { data: clientsData } = useQuery(PUBLIC_CLIENT_LIST_QUERY, {
    variables: {
      where: {
        publicCompanyId: companyId,
        isActive: true
      }
    },
    skip: !companyId,
  });

  const isEditing = !!editingComment;

  const formatCurrentDate = () => {
    const now = new Date();
    return now.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + ', ' + now.toLocaleTimeString(i18next.language, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEditDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18next.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }) + ', ' + date.toLocaleTimeString(i18next.language, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const mapDbContactTypeToUi = (dbValue?: string | null): 'INFO' | 'PERS' | 'EMAIL' | 'TEL' => {
    if (dbValue === 'PERS' || dbValue === 'EMAIL' || dbValue === 'TEL') return dbValue;
    return 'INFO';
  };

  useEffect(() => {
    if (editingComment) {
      setDescription(editingComment.description);
      setIsBookingRelevant(editingComment.isBookingRelevant);
      setIsContactRelevant(!!editingComment.isContactRelevant);
      setContactSubjectId(editingComment.contactSubjectId || null);
      setSelectedClientId(editingComment.publicClient?.id || null);
      setContactType(mapDbContactTypeToUi(editingComment.contactType));
    } else {
      setDescription('');
      setIsBookingRelevant(false);
      setIsContactRelevant(false);
      setContactSubjectId(null);
      setSelectedClientId(clientId || null);
      setContactType('INFO');
    }
    setErrors({});
  }, [editingComment, open, clientId]);

  const validateForm = () => {
    const newErrors: { description?: string; contactSubject?: string } = {};

    if (!description.trim()) {
      newErrors.description = String(i18next.t('comment-dialog-validation-description-required'));
    } else if (description.trim().length < 10) {
      newErrors.description = i18next.t('publiccompany-comment-validation-min') as string;
    }

    if (isContactRelevant && !contactSubjectId) {
      newErrors.contactSubject = String(i18next.t('comment-dialog-validation-subject-required'));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const commentData: any = {
        description: description.trim(),
        isBookingRelevant,
        isContactRelevant,
        contactType: isContactRelevant ? contactType : null,
        contactSubjectId: isContactRelevant ? contactSubjectId : null,
        publicCompanyId: companyId,
        publicClientId: selectedClientId,
      };

      if (isEditing) {
        await updateCommentMutation({
          variables: {
            id: editingComment!.id,
            data: commentData,
          },
        });
        dispatchMessage(dispatch, i18next.t('publiccompany-comment-save-success'));
      } else {
        await createCommentMutation({
          variables: {
            data: commentData,
          },
        });
        dispatchMessage(dispatch, i18next.t('publiccompany-comment-save-success'));
      }

      onSaved();
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const handleClose = () => {
    setDescription('');
    setIsBookingRelevant(false);
    setIsContactRelevant(false);
    setContactType('INFO');
    setContactSubjectId(null);
    setSelectedClientId(null);
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">
          {i18next.t('comment-dialog-title-edit')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={1}>
          {/* Creation info */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>{String(i18next.t('comment-dialog-created-at'))}:</strong> {isEditing ? formatEditDate(editingComment!.createdAt) : formatCurrentDate()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{String(i18next.t('comment-dialog-created-by'))}:</strong>{' '}
              {isEditing
                ? editingComment!.createdBy?.name || i18next.t('comment-dialog-unknown-user')
                : currentUser?.name || i18next.t('comment-dialog-current-user')}
            </Typography>
          </Box>

          {/* Booking relevant checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isBookingRelevant}
                onChange={(e) => setIsBookingRelevant(e.target.checked)}
                color="warning"
              />
            }
            label={String(i18next.t('comment-dialog-booking-relevant'))}
          />

          {/* Contact relevant checkbox */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isContactRelevant}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsContactRelevant(checked);
                    if (!checked) {
                      setContactSubjectId(null);
                      setContactType('INFO');
                    } else {
                      setContactType('INFO');
                    }
                  }}
                  color="info"
                />
              }
              label={String(i18next.t('comment-dialog-contact-relevant'))}
            />

            <FormControl sx={{ minWidth: 180 }} disabled={!isContactRelevant}>
              <InputLabel>{i18next.t('comment-dialog-contact-type')}</InputLabel>
              <Select
                value={contactType}
                label={String(i18next.t('comment-dialog-contact-type'))}
                onChange={(e) => setContactType(e.target.value as 'INFO' | 'PERS' | 'EMAIL' | 'TEL')}
              >
                {contactTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {String(i18next.t(opt.labelKey))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Contact Subject selector - enabled only when contact relevant */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <FormControl fullWidth disabled={!isContactRelevant} error={!!errors.contactSubject}>
              <InputLabel>{String(i18next.t('comment-dialog-subject'))}{isContactRelevant ? ' *' : ''}</InputLabel>
              <Select
                value={contactSubjectId || ''}
                onChange={(e) => setContactSubjectId(e.target.value ? Number(e.target.value) : null)}
                label={isContactRelevant ? String(i18next.t('comment-dialog-subject-required')) : String(i18next.t('comment-dialog-subject'))}
              >
                <MenuItem value="">
                  <em>{i18next.t('comment-dialog-no-subject')}</em>
                </MenuItem>
                {contactSubjects.map((subject: any) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.contactSubject && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.contactSubject}
                </Typography>
              )}
            </FormControl>
            <IconButton
              onClick={() => refetchContactSubjects()}
              title={String(i18next.t('comment-dialog-subjects-refresh'))}
              sx={{ mt: 1 }}
            >
              <RefreshIcon />
            </IconButton>
            {currentUser?.isRoot && (
              <IconButton
                onClick={() => window.open('/administration', '_blank')}
                title={String(i18next.t('comment-dialog-subjects-manage'))}
                sx={{ mt: 1 }}
              >
                <SettingsIcon />
              </IconButton>
            )}
          </Box>

          {/* Contact person selector - only show when not already in client context */}
          {!clientId && (
            <FormControl fullWidth>
              <InputLabel>{i18next.t('comment-dialog-contact-person')}</InputLabel>
              <Select
                value={selectedClientId || ''}
                onChange={(e) => setSelectedClientId(e.target.value ? Number(e.target.value) : null)}
                label={String(i18next.t('comment-dialog-contact-person'))}
              >
                <MenuItem value="">
                  <em>{i18next.t('comment-dialog-no-contact-person')}</em>
                </MenuItem>
                {clientsData?.listPublicClients?.clients?.map((client: any) => (
                  <MenuItem key={client.id} value={client.id}>
                    {`${client.firstname || ''} ${client.lastname || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Description text field */}
          <TextField
            label={String(i18next.t('comment-dialog-description-required'))}
            multiline
            minRows={4}
            maxRows={10}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            variant="outlined"
            placeholder={String(i18next.t('comment-dialog-description-placeholder'))}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {i18next.t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!description.trim() || (isContactRelevant && !contactSubjectId)}
        >
          {i18next.t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommentDialog;
